import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiTrash2, FiEdit, FiUpload, FiX, FiCheck, FiArrowLeft, FiBarChart2 } from 'react-icons/fi';

const SeoPositions = () => {
  const { currentLang, fetchWithAuth, API_URL, getCsrfToken } = useAuth();
  const navigate = useNavigate();
  
  // Состояние для сайтов
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState('');

  // Загрузка сайтов при монтировании компонента
  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      setSitesLoading(true);
      setSitesError('');
      
      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites`);

      const data = await response.json();

      if (response.ok && data.success) {
        setSites(data.sites || []);
      } else {
        throw new Error(data.message || 'Ошибка загрузки сайтов');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setSitesError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setSitesError(error.message);
      }
    } finally {
      setSitesLoading(false);
    }
  };

  const handleSiteSelect = (site) => {
    navigate(`/seo/positions/${site.id}`);
  };

  return (
    <div className="mt-8 sm:mt-12 mb-4 sm:mb-8 flex flex-col gap-6 sm:gap-12">
      <div className="bg-white rounded-lg sm:rounded-xl shadow-lg sm:shadow-xl dark:bg-navy-800">
        <div className="bg-gradient-to-r from-gray-100 to-blue-100 rounded-t-lg sm:rounded-t-xl p-4 sm:p-6 mb-4 sm:mb-8">
          <h1 className="text-lg sm:text-xl font-bold text-blue-500">
            SEO Позиции - Выбор сайта
          </h1>
          <p className="text-gray-900 mt-1 sm:mt-2 text-sm sm:text-base">
            Выберите сайт для управления SEO позициями
          </p>
        </div>
        <div className="p-3 sm:p-6">
          {sitesError && (
            <div className="mb-4 sm:mb-6">
              <div className="bg-red-100 border border-red-400 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded text-sm dark:bg-red-900 dark:border-red-700 dark:text-red-200">
                {sitesError}
              </div>
            </div>
          )}

          {sitesLoading ? (
            <div className="flex justify-center items-center py-6 sm:py-8">
              <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-500"></div>
              <p className="ml-2 sm:ml-3 text-gray-600 text-sm sm:text-base">Загрузка сайтов...</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:gap-4">
              {sites.map((site) => (
                <div key={site.id} className="bg-white rounded-lg shadow-md p-4 sm:p-6 border dark:bg-navy-700 dark:text-white dark:border-white/10 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => handleSiteSelect(site)}>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0 mb-3 sm:mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 flex flex-wrap items-center gap-1 sm:gap-2">
                        <img src={`https://www.google.com/s2/favicons?domain=${site.dns}`} alt="favicon" className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span className="truncate">{site.dns}</span>
                        <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-bold rounded-full flex-shrink-0 ${
                          site.https === 1 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {site.https === 1 ? 'HTTPS' : 'HTTP'}
                        </span>
                      </h3>
                    </div>
                    <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
                      <FiBarChart2 className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
                      <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">SEO Позиции</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Нажмите для управления позициями
                    </p>
                    <div className="text-blue-500">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!sitesLoading && sites.length === 0 && (
            <div className="text-center py-6 sm:py-8">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Сайты не найдены
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4 px-2">
                Сначала добавьте сайты в разделе SEO Сайты
              </p>
              {sitesError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded text-sm">
                  Ошибка: {sitesError}
                </div>
              )}
              <div className="text-xs text-gray-500 mt-2 px-2">
                Состояние загрузки: {sitesLoading ? 'Загружается' : 'Завершено'}, 
                Количество сайтов: {sites.length}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeoPositions; 